import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CaretDown,
  CaretUp,
  Copy,
  Plus,
  Trash,
  Users,
} from '@phosphor-icons/react';
import {
  IPhysicalWorkRefItem,
  ICoachSession,
  ICoachSessionAthlete,
  coachSessionsEndpoints,
} from '../../../endpoints/coachSessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { AddPhysicalRefDialog } from './AddPhysicalRefDialog';
import styles from './PhysicalRefsList.module.scss';

interface PhysicalRefsListProps {
  sessionId: string;
  blockOrder: number;
  refs: IPhysicalWorkRefItem[];
  rosterAthletes?: ICoachSessionAthlete[];
}

/**
 * Bug 69f87a4b — Lista delle schede palestra di un blocco fisico.
 * Card collassabili, picker atleti, validazione no-overlap, duplica/sposta.
 */
export const PhysicalRefsList: FC<PhysicalRefsListProps> = ({
  sessionId,
  blockOrder,
  refs,
  rosterAthletes: rosterFromProps,
}) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingAthletesFor, setEditingAthletesFor] = useState<string | null>(
    null,
  );

  // Fallback: se le props non sono passate (HMR stale o prop drilling rotto),
  // leggiamo direttamente dalla cache TanStack della session.
  const sessionFromCache = useQuery<ICoachSession>({
    queryKey: ['coachSession', sessionId],
    queryFn: () =>
      coachSessionsEndpoints.get(sessionId).then((r) => r.data),
    enabled: !!sessionId,
  });
  const rosterAthletes: ICoachSessionAthlete[] =
    rosterFromProps && rosterFromProps.length > 0
      ? rosterFromProps
      : (sessionFromCache.data?.athletes ?? []);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['coachSession', sessionId] });

  const sorted = useMemo(
    () => [...refs].sort((a, b) => a.order - b.order),
    [refs],
  );

  const takenByOther = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    sorted.forEach((r) => {
      map[r.refId] = new Set();
      sorted
        .filter((o) => o.refId !== r.refId)
        .forEach((o) => o.assignedAthleteIds.forEach((id) => map[r.refId].add(id)));
    });
    return map;
  }, [sorted]);

  const deleteMut = useMutation({
    mutationFn: (refId: string) =>
      coachSessionsEndpoints
        .deletePhysicalWorkRef(sessionId, blockOrder, refId)
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  const duplicateMut = useMutation({
    mutationFn: (refId: string) =>
      coachSessionsEndpoints
        .duplicatePhysicalWorkRef(sessionId, blockOrder, refId)
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  const reorderMut = useMutation({
    mutationFn: (orderedRefIds: string[]) =>
      coachSessionsEndpoints
        .reorderPhysicalWorkRefs(sessionId, blockOrder, { orderedRefIds })
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: (vars: { refId: string; assignedAthleteIds: string[] }) =>
      coachSessionsEndpoints
        .updatePhysicalWorkRef(sessionId, blockOrder, vars.refId, {
          assignedAthleteIds: vars.assignedAthleteIds,
        })
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  const move = (refId: string, dir: -1 | 1) => {
    const idx = sorted.findIndex((r) => r.refId === refId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[target]] = [next[target], next[idx]];
    reorderMut.mutate(next.map((r) => r.refId));
  };

  const toggleAthlete = (ref: IPhysicalWorkRefItem, athleteId: string) => {
    const current = new Set(ref.assignedAthleteIds);
    if (current.has(athleteId)) current.delete(athleteId);
    else if (!takenByOther[ref.refId]?.has(athleteId)) current.add(athleteId);
    updateMut.mutate({
      refId: ref.refId,
      assignedAthleteIds: Array.from(current),
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.headerLabel}>
          Schede palestra ({sorted.length})
        </span>
        <CustomButton
          color="primary"
          size="sm"
          onClick={() => setDialogOpen(true)}
        >
          <Plus size={14} weight="bold" /> Aggiungi scheda
        </CustomButton>
      </div>

      {sorted.length === 0 && (
        <div className={styles.empty}>
          Nessuna scheda. Aggiungi la prima per assegnare un sottogruppo del
          team.
        </div>
      )}

      <div className={styles.cardList}>
        {sorted.map((ref, idx) => {
          const isExpanded = expanded[ref.refId] ?? false;
          const isEditing = editingAthletesFor === ref.refId;
          const snapTitle =
            ref.displaySnapshot?.title ?? 'Scheda palestra';
          const snapDur =
            ref.displaySnapshot?.duration ??
            ref.displaySnapshot?.durationMinutes;
          const snapEx = ref.displaySnapshot?.exerciseCount;
          return (
            <div key={ref.refId} className={styles.card}>
              <div className={styles.cardRow}>
                <button
                  type="button"
                  className={styles.expandBtn}
                  onClick={() =>
                    setExpanded((s) => ({ ...s, [ref.refId]: !isExpanded }))
                  }
                  aria-label={isExpanded ? 'Comprimi' : 'Espandi'}
                >
                  {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
                <div className={styles.cardInfo}>
                  <span className={styles.cardTitle}>{snapTitle}</span>
                  <span className={styles.cardMeta}>
                    <Users size={12} weight="bold" />{' '}
                    {ref.assignedAthleteIds.length} atlet
                    {ref.assignedAthleteIds.length === 1 ? 'a' : 'i'}
                    {snapDur != null && ` · ${snapDur}'`}
                    {snapEx != null && ` · ${snapEx} esercizi`}
                  </span>
                </div>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    disabled={idx === 0 || reorderMut.isPending}
                    onClick={() => move(ref.refId, -1)}
                    title="Sposta su"
                  >
                    <CaretUp size={12} />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    disabled={
                      idx === sorted.length - 1 || reorderMut.isPending
                    }
                    onClick={() => move(ref.refId, 1)}
                    title="Sposta giù"
                  >
                    <CaretDown size={12} />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    disabled={duplicateMut.isPending}
                    onClick={() => duplicateMut.mutate(ref.refId)}
                    title="Duplica"
                  >
                    <Copy size={12} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                    disabled={deleteMut.isPending}
                    onClick={() => deleteMut.mutate(ref.refId)}
                    title="Elimina"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className={styles.cardBody}>
                  <div className={styles.bodyRow}>
                    <span className={styles.bodyLabel}>
                      Atleti assegnati ({ref.assignedAthleteIds.length})
                    </span>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() =>
                        setEditingAthletesFor(isEditing ? null : ref.refId)
                      }
                    >
                      {isEditing ? 'Chiudi' : 'Modifica'}
                    </button>
                  </div>
                  {!isEditing && (
                    <div className={styles.athleteChips}>
                      {ref.assignedAthleteIds.length === 0 && (
                        <span className={styles.muted}>
                          Nessun atleta assegnato.
                        </span>
                      )}
                      {ref.assignedAthleteIds.map((aid) => {
                        const a = rosterAthletes.find(
                          (r) => r.athleteId === aid,
                        );
                        return (
                          <span key={aid} className={styles.chip}>
                            {a
                              ? `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() ||
                                aid.slice(-6)
                              : `…${aid.slice(-6)}`}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {isEditing && (
                    <div className={styles.athletePicker}>
                      {rosterAthletes.map((a) => {
                        const checked = ref.assignedAthleteIds.includes(
                          a.athleteId,
                        );
                        const taken = takenByOther[ref.refId]?.has(a.athleteId);
                        return (
                          <label
                            key={a.athleteId}
                            className={`${styles.athleteRow} ${
                              taken ? styles.athleteRowTaken : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={taken && !checked}
                              onChange={() =>
                                toggleAthlete(ref, a.athleteId)
                              }
                            />
                            <span>
                              {`${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() ||
                                a.athleteId.slice(-6)}
                            </span>
                            {taken && !checked && (
                              <span className={styles.takenLabel}>
                                già in altra scheda
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {dialogOpen && (
        <AddPhysicalRefDialog
          sessionId={sessionId}
          blockOrder={blockOrder}
          rosterAthletes={rosterAthletes}
          alreadyAssigned={
            new Set(sorted.flatMap((r) => r.assignedAthleteIds))
          }
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
};
